import { useState, useMemo, useCallback, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { qPatientFiles, qPatientFilesByEncounter, type PatientFileRow } from '@helios/data-access';
import { UploadDrawer } from './UploadDrawer';
import {
  type PatientFileItem,
  type FileFilterChip,
  FILE_FILTER_CHIPS,
  chipToTag,
  tagLabel,
  formatDateTime,
  formatDateShort,
} from './consultation-data';

interface FilesTabProps {
  patientId: string;
  encounterId?: string;
  visitId?: string;
}

export function FilesTab({ patientId, encounterId, visitId }: FilesTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#78716c';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const fileQuery = encounterId
    ? qPatientFilesByEncounter(patientId, encounterId)
    : qPatientFiles(patientId);
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(fileQuery);

  // Map DB rows to UI type
  const files: PatientFileItem[] = useMemo(
    () =>
      fileRows.map((r) => ({
        id: r.id,
        patientId: r.patient_id ?? '',
        kind: (r.kind ?? 'document') as 'image' | 'document',
        filename: r.filename ?? '',
        url: r.url ?? undefined,
        tag: (r.tag ?? 'other') as PatientFileItem['tag'],
        createdAt: r.created_at ?? '',
        starred: r.starred === 1,
        mimeType: r.mime_type ?? undefined,
        description: r.description ?? undefined,
      })),
    [fileRows],
  );

  const [search, setSearch] = useState('');
  const [activeChip, setActiveChip] = useState<FileFilterChip>('All');
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [viewMode, setViewMode] = useState<'grid-2' | 'grid' | 'list'>('grid-2');
  const [subTab, setSubTab] = useState<'images' | 'documents'>('images');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = files.filter((f) =>
      subTab === 'images' ? f.kind === 'image' : f.kind === 'document',
    );

    if (activeChip === 'Favorites') {
      result = result.filter((f) => f.starred);
    } else {
      const tagFilter = chipToTag[activeChip];
      if (tagFilter) {
        result = result.filter((f) => f.tag === tagFilter);
      }
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (f) =>
          f.filename.toLowerCase().includes(q) ||
          f.tag.toLowerCase().includes(q) ||
          (f.description ?? '').toLowerCase().includes(q),
      );
    }

    result.sort((a, b) => {
      const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return sort === 'newest' ? diff : -diff;
    });

    return result;
  }, [files, search, activeChip, sort, subTab]);

  const toggleStar = useCallback(
    async (id: string) => {
      if (!db) return;
      const file = files.find((f) => f.id === id);
      if (!file) return;
      const newVal = file.starred ? 0 : 1;
      await db.execute(`UPDATE patient_files SET starred = ?, updated_at = ? WHERE id = ?`, [
        newVal,
        new Date().toISOString(),
        id,
      ]);
    },
    [db, files],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_files WHERE id = ?`, [id]);
    },
    [db],
  );

  const handleUpload = useCallback(
    async (file: PatientFileItem) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_files
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, kind, filename, url, tag, mime_type, description, starred, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          null,
          patientId,
          encounterId ?? null,
          visitId ?? null,
          file.kind,
          file.filename,
          file.url ?? null,
          file.tag,
          file.mimeType ?? null,
          file.description ?? null,
          file.starred ? 1 : 0,
          now,
          now,
        ],
      );
    },
    [db, orgId, patientId, encounterId, visitId],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* Top controls row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          flexWrap: 'wrap',
        }}
      >
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x) 0 40px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Filter chips */}
        {FILE_FILTER_CHIPS.map((chip) => {
          const isActive = activeChip === chip;
          return (
            <button
              key={chip}
              type="button"
              onClick={() => setActiveChip(chip)}
              style={{
                height: 32,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-full)',
                border: isActive ? 'none' : '1px solid var(--border-input)',
                background: isActive ? primary : 'var(--bg-surface)',
                color: isActive ? '#fff' : 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {chip}
            </button>
          );
        })}

        {/* Sort */}
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as 'newest' | 'oldest')}
          style={selectStyle}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>

        {/* View toggle */}
        <div
          style={{
            display: 'flex',
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          {/* 2-column grid */}
          <button
            type="button"
            onClick={() => setViewMode('grid-2')}
            style={{
              width: 36,
              height: 34,
              border: 'none',
              background: viewMode === 'grid-2' ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
              color: viewMode === 'grid-2' ? 'var(--text)' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1" y="1" width="6" height="6" rx="1" />
              <rect x="9" y="1" width="6" height="6" rx="1" />
              <rect x="1" y="9" width="6" height="6" rx="1" />
              <rect x="9" y="9" width="6" height="6" rx="1" />
            </svg>
          </button>
          {/* Auto-fill grid */}
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            style={{
              width: 36,
              height: 34,
              border: 'none',
              borderLeft: '1px solid var(--border-input)',
              background: viewMode === 'grid' ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
              color: viewMode === 'grid' ? 'var(--text)' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <NavIcon name="grid" />
          </button>
          {/* List view */}
          <button
            type="button"
            onClick={() => setViewMode('list')}
            style={{
              width: 36,
              height: 34,
              border: 'none',
              borderLeft: '1px solid var(--border-input)',
              background: viewMode === 'list' ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
              color: viewMode === 'list' ? 'var(--text)' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <NavIcon name="list" />
          </button>
        </div>

        {/* Upload */}
        <Button variant="gradient" size="md" onClick={() => setUploadOpen(true)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NavIcon name="upload" />
            Upload
          </span>
        </Button>
      </div>

      {/* Sub-tabs: Images / Documents */}
      <div style={{ display: 'flex', gap: 'var(--sp-1)', borderBottom: '1px solid var(--border)' }}>
        {(['images', 'documents'] as const).map((tab) => {
          const isActive = subTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setSubTab(tab)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                padding: 'var(--sp-2) var(--sp-4)',
                border: 'none',
                borderBottom: isActive ? `2px solid ${primary}` : '2px solid transparent',
                background: 'none',
                color: isActive ? 'var(--text)' : 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: isActive ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              <NavIcon name={tab === 'images' ? 'image' : 'file-text'} />
              {tab === 'images' ? 'Images' : 'Documents'}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <EmptyState kind={subTab} onUpload={() => setUploadOpen(true)} />
      ) : subTab === 'images' && (viewMode === 'grid' || viewMode === 'grid-2') ? (
        <ImagesGrid
          files={filtered}
          columns={viewMode === 'grid-2' ? 2 : 1}
          onToggleStar={toggleStar}
          onDelete={handleDelete}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
        />
      ) : (
        <FilesTable
          files={filtered}
          onToggleStar={toggleStar}
          onDelete={handleDelete}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
        />
      )}

      <UploadDrawer
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUpload}
        orgId={orgId}
        patientId={patientId}
        brandTokens={brandTokens}
      />
    </div>
  );
}

// ─── Images Grid ───

function ImagesGrid({
  files,
  columns,
  onToggleStar,
  onDelete,
  openMenu,
  setOpenMenu,
}: {
  files: PatientFileItem[];
  columns?: number;
  onToggleStar: (id: string) => void;
  onDelete: (id: string) => void;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: columns
          ? `repeat(${columns}, 1fr)`
          : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {files.map((file) => (
        <ImageCard
          key={file.id}
          file={file}
          onToggleStar={() => onToggleStar(file.id)}
          onDelete={() => onDelete(file.id)}
          menuOpen={openMenu === file.id}
          onMenuToggle={() => setOpenMenu(openMenu === file.id ? null : file.id)}
          onMenuClose={() => setOpenMenu(null)}
        />
      ))}
    </div>
  );
}

function ImageCard({
  file,
  onToggleStar,
  onDelete,
  menuOpen,
  onMenuToggle,
  onMenuClose,
}: {
  file: PatientFileItem;
  onToggleStar: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
        background: 'var(--bg-surface)',
      }}
    >
      {/* Image preview */}
      <div style={{ position: 'relative', aspectRatio: '4/3', background: '#f1f5f9' }}>
        {file.url ? (
          <img
            src={file.url}
            alt={file.filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="image" />
          </div>
        )}

        {/* Top-right actions overlay */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 4,
          }}
        >
          <button type="button" onClick={onToggleStar} style={overlayBtnStyle}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={file.starred ? '#f59e0b' : 'none'}
              stroke={file.starred ? '#f59e0b' : 'currentColor'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={onMenuToggle} style={overlayBtnStyle}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="5" r="1" />
                <circle cx="12" cy="12" r="1" />
                <circle cx="12" cy="19" r="1" />
              </svg>
            </button>
            {menuOpen && <KebabMenu onClose={onMenuClose} onDelete={onDelete} />}
          </div>
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: 'var(--sp-3)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            marginBottom: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {file.filename}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            marginBottom: 6,
          }}
        >
          {formatDateTime(file.createdAt)}
        </div>
        <TagPill tag={file.tag} />
      </div>
    </div>
  );
}

// ─── Files Table (list view for images or documents) ───

function FilesTable({
  files,
  onToggleStar,
  onDelete,
  openMenu,
  setOpenMenu,
}: {
  files: PatientFileItem[];
  onToggleStar: (id: string) => void;
  onDelete: (id: string) => void;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {['NAME', 'TYPE', 'TAG', 'DATE', 'ACTIONS'].map((col) => (
            <th key={col} style={thStyle}>
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {files.map((file) => (
          <tr key={file.id} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={tdStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-md)',
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    flexShrink: 0,
                  }}
                >
                  <NavIcon name={file.kind === 'image' ? 'image' : 'file-text'} />
                </div>
                <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                  {file.filename}
                </span>
              </div>
            </td>
            <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
              {file.mimeType?.split('/')[1]?.toUpperCase() ||
                (file.kind === 'image' ? 'JPG' : 'PDF')}
            </td>
            <td style={tdStyle}>
              <TagPill tag={file.tag} />
            </td>
            <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
              {formatDateShort(file.createdAt)}
            </td>
            <td style={{ ...tdStyle, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <button
                  type="button"
                  onClick={() => onToggleStar(file.id)}
                  style={{
                    width: 28,
                    height: 28,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    color: file.starred ? '#f59e0b' : 'var(--text-muted)',
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill={file.starred ? '#f59e0b' : 'none'}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setOpenMenu(openMenu === file.id ? null : file.id)}
                    style={{
                      width: 28,
                      height: 28,
                      border: 'none',
                      background: openMenu === file.id ? 'var(--bg-surface-2)' : 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      color: 'var(--text-muted)',
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                  {openMenu === file.id && (
                    <KebabMenu
                      onClose={() => setOpenMenu(null)}
                      onDelete={() => onDelete(file.id)}
                    />
                  )}
                </div>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Shared sub-components ───

function TagPill({ tag }: { tag: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    other: { bg: '#f1f5f9', text: '#475569' },
    consent: { bg: '#dbeafe', text: '#1d4ed8' },
    lab: { bg: '#fef3c7', text: '#92400e' },
    before_after: { bg: '#fce7f3', text: '#be185d' },
    images: { bg: '#d1fae5', text: '#065f46' },
    documents: { bg: '#e0e7ff', text: '#3730a3' },
  };
  const c = colors[tag] ?? colors.other;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.text,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
      }}
    >
      {tagLabel(tag)}
    </span>
  );
}

function KebabMenu({ onClose, onDelete }: { onClose: () => void; onDelete?: () => void }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={onClose} />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '100%',
          marginTop: 4,
          width: 160,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 10,
          overflow: 'hidden',
        }}
      >
        {['View details', 'Download', 'Delete'].map((action) => (
          <button
            key={action}
            type="button"
            onClick={() => {
              if (action === 'Delete' && onDelete) onDelete();
              onClose();
            }}
            style={{
              width: '100%',
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'none',
              border: 'none',
              textAlign: 'left',
              fontSize: 'var(--font-size-sm)',
              color: action === 'Delete' ? '#dc2626' : 'var(--text)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.background = 'var(--bg-surface-2)';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = 'none';
            }}
          >
            {action}
          </button>
        ))}
      </div>
    </>
  );
}

function EmptyState({ kind, onUpload }: { kind: 'images' | 'documents'; onUpload: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--sp-12)',
        color: 'var(--text-muted)',
        gap: 'var(--sp-3)',
      }}
    >
      <NavIcon name={kind === 'images' ? 'image' : 'file-text'} />
      <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semi)' }}>
        No {kind} found
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)' }}>
        Upload your first {kind === 'images' ? 'image' : 'document'} to get started
      </span>
      <Button variant="gradient" size="md" onClick={onUpload} style={{ marginTop: 'var(--sp-3)' }}>
        Upload
      </Button>
    </div>
  );
}

// ─── Styles ───

const overlayBtnStyle: CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(4px)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#475569',
  padding: 0,
};

const thStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  textAlign: 'left',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};
